<template>
  <div class="app-shell">
    <aside class="sidebar">
      <div class="brand">
        <div class="brand-mark">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="brand-text">
          <span class="brand-title">SkyNET</span>
          <span class="brand-subtitle">Control Center</span>
        </div>
      </div>

      <nav class="main-nav">
        <RouterLink class="nav-item" exact-active-class="active" to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
          <span>Dashboard</span>
        </RouterLink>
        <RouterLink class="nav-item" active-class="active" to="/queue">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          <span>Queue</span>
        </RouterLink>
        <RouterLink class="nav-item" active-class="active" to="/cameras">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="23 7 16 12 23 17 23 7"/>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
          </svg>
          <span>Cameras</span>
        </RouterLink>
        <RouterLink v-if="isAdmin" class="nav-item" active-class="active" to="/settings">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          <span>Settings</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer">
        <div class="connection-status" :class="{ online: socketConnected }">
          <span class="status-dot"></span>
          <span class="status-label">{{ socketConnected ? 'Live' : 'Offline' }}</span>
        </div>
      </div>
    </aside>

    <main class="main-content">
      <header class="top-bar">
        <h2 class="page-title">{{ pageTitle }}</h2>
        <div class="user-badge">
          <div class="user-avatar">
            <span>{{ userInitials }}</span>
          </div>
          <div class="user-info">
            <span class="user-label">Operator</span>
            <span class="user-id">ID: {{ user_id || '—' }}</span>
          </div>
        </div>
      </header>

      <div class="content-wrapper">
        <div v-if="!access" class="access-denied">
          <div class="denied-card">
            <div class="denied-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h3>Access Denied</h3>
            <p>{{ error || 'Authentication required. Please provide a valid access token.' }}</p>
          </div>
        </div>
        <RouterView v-else />
      </div>
    </main>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { getTg, isUserAdmin } from '@/utils/telegram'

export default {
  name: 'App',
  data() {
    return {
      access: true,
      error: '',
      counter: 0,
      isAdmin: false,
      user_id: 0,
      socketConnected: false,
      tg: null
    }
  },
  computed: {
    pageTitle() {
      const titles = {
        '/': 'Dashboard',
        '/queue': 'Task Queue',
        '/cameras': 'Cameras',
        '/settings': 'System Settings'
      }
      return titles[this.$route.path] || 'Dashboard'
    },
    userInitials() {
      return this.user_id ? String(this.user_id).slice(0, 2).toUpperCase() : 'OP'
    }
  },
  sockets: {
    connect() {
      this.socketConnected = true
    },
    disconnect() {
      this.socketConnected = false
    },
    hello(data) {
      this.counter = data
    }
  },
  methods: {
    switcher() {
      const tg = this.tg
      if (tg && tg.MainButton) {
        if (tg.MainButton.isVisible) tg.MainButton.hide()
        else tg.MainButton.show()
      }
    }
  },
  mounted() {
    const tg = getTg()
    if (tg && typeof tg.ready === 'function') tg.ready()

    this.tg = tg ? markRaw(tg) : null

    if (this.tg && this.tg.MainButton) {
      if (typeof this.tg.MainButton.setText === 'function') {
        this.tg.MainButton.setText('Changed Text')
      } else {
        this.tg.MainButton.text = 'Changed Text'
      }

      const user = this.tg.initDataUnsafe?.user
      this.user_id = user?.id || 0
    }

    this.isAdmin = isUserAdmin()

    this.access = false
    if (this.$auth != null) {
      this.axios.get(process.env.VUE_APP_BACK_URL + '/auth?token=' + this.$auth)
          .then(res => { this.access = res.data })
          .catch(err => {
            console.log(err.message)
            this.error = err.message
            this.access = false
          })
    }
  }
}
</script>

<style>
:root {
  --bg-primary: #05070a;
  --bg-secondary: #0b1017;
  --bg-tertiary: #111827;
  --surface: rgba(17, 24, 39, 0.7);
  --surface-hover: rgba(31, 41, 55, 0.8);
  --border: rgba(148, 163, 184, 0.12);
  --border-strong: rgba(148, 163, 184, 0.2);
  --text-primary: #f1f5f9;
  --text-secondary: #94a3b8;
  --text-muted: #64748b;
  --accent: #00d4ff;
  --accent-dim: rgba(0, 212, 255, 0.12);
  --success: #00e676;
  --warning: #ff9100;
  --danger: #ff5252;
  --danger-bg: rgba(255, 82, 82, 0.1);
  --font-display: 'Chakra Petch', sans-serif;
  --font-body: 'Sora', sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
  --radius: 14px;
  --shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  --glow: 0 0 20px rgba(0, 212, 255, 0.15);
}

* {
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  background:
      radial-gradient(ellipse at 10% 20%, rgba(0, 212, 255, 0.06) 0%, transparent 40%),
      radial-gradient(ellipse at 90% 80%, rgba(0, 230, 118, 0.04) 0%, transparent 40%),
      var(--bg-primary);
}

#app {
  width: 100%;
  height: 100%;
}

.app-shell {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
  border-right: 1px solid var(--border);
  padding: 28px 20px 20px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 44px;
}

.brand-mark {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  background: var(--accent-dim);
  border: 1px solid rgba(0, 212, 255, 0.25);
  border-radius: 12px;
  box-shadow: var(--glow);
}

.brand-mark svg {
  width: 24px;
  height: 24px;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.brand-title {
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--text-primary);
}

.brand-subtitle {
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-top: 4px;
}

.main-nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  border-radius: 10px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.nav-item svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-item:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}

.nav-item.active {
  background: var(--accent-dim);
  color: var(--accent);
  border-color: rgba(0, 212, 255, 0.25);
  box-shadow: var(--glow);
}

.sidebar-footer {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.connection-status.online {
  color: var(--success);
  background: rgba(0, 230, 118, 0.08);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 8px currentColor;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.top-bar {
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  background: rgba(5, 7, 10, 0.7);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
}

.page-title {
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
}

.user-badge {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), #0066ff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.85rem;
  color: #fff;
  box-shadow: 0 0 16px rgba(0, 212, 255, 0.25);
}

.user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.user-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

.user-id {
  font-family: var(--font-mono);
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.content-wrapper {
  flex: 1;
  overflow: auto;
  padding: 28px 32px;
}

.access-denied {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.denied-card {
  text-align: center;
  padding: 48px 56px;
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  max-width: 460px;
}

.denied-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  color: var(--danger);
}

.denied-card h3 {
  font-family: var(--font-display);
  font-size: 1.4rem;
  margin: 0 0 10px;
}

.denied-card p {
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 860px) {
  .sidebar {
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    height: auto;
    flex-direction: row;
    align-items: center;
    padding: 10px 16px;
    z-index: 100;
    border-right: none;
    border-top: 1px solid var(--border);
  }

  .brand,
  .sidebar-footer {
    display: none;
  }

  .main-nav {
    flex-direction: row;
    justify-content: space-around;
    gap: 0;
  }

  .nav-item {
    flex-direction: column;
    padding: 8px 12px;
    font-size: 0.7rem;
    gap: 5px;
  }

  .nav-item svg {
    width: 22px;
    height: 22px;
  }

  .main-content {
    padding-bottom: 72px;
  }

  .top-bar {
    padding: 0 20px;
  }

  .content-wrapper {
    padding: 20px;
  }
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--border-strong);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}
</style>
